import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import structureTypes from 'common/constants/structureTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import { Entities } from 'common/utils/entityUtils'
import * as actions from 'client/actions/actionsManagement'
import { useSetEntityHighlighted } from 'client/context/HighlightedContext'
import { useManagement, usePage } from 'client/store'
import managementSelectors from 'client/store/management/managementSelectors'
import pageSelectors from 'client/store/page/pageSelectors'
import { twMerge } from 'tailwind-merge'

const averageBreadcrumbWidth = 120

const omitEntityTypes = (entity: Entities[number]) =>
  [
    structureTypes.TWO_STEP_PAYMENT_FORM_STEP_PAYMENT,
    structureTypes.TWO_STEP_PAYMENT_FORM_STEP_OPT_IN,
    structureTypes.COLUMN,
    EntityTypeEnum.Column,
    EntityTypeEnum.BlogPageBody,
    EntityTypeEnum.FaqItem,
    EntityTypeEnum.CarouselItem,
    structureTypes.CAROUSEL_ITEM,
  ].includes(entity.type) === false

export const Breadcrumbs = () => {
  const ref = useRef<HTMLDivElement | null>()
  const [allowedItemsCount, setAllowedItemsCount] = useState<number | null>(
    null,
  )
  const editingId = useManagement(managementSelectors.getEditingId)
  const breadcrumbs = usePage(page =>
    pageSelectors.getBreadcrumbs(page, editingId || ''),
  )

  const setHighlightedId = useSetEntityHighlighted()
  const toggleSettingsAction = useActions(actions.toggleSettings)
  const { t } = useTranslation()
  const highlight =
    (id = '') =>
    () =>
      setHighlightedId(id)
  const toggleSettings = (id: string) => () => toggleSettingsAction(id)

  const setRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      if (node && node.parentElement) {
        setAllowedItemsCount(
          Math.floor(
            (node.parentElement.offsetWidth - 300 - 220) /
              averageBreadcrumbWidth,
          ),
        )
      }
    }
    // Save a reference to the node
    ref.current = node
  }, [])

  const breadcrumbsCount = Object.keys(breadcrumbs).length

  return (
    <div
      ref={setRef}
      className={twMerge(
        'items-center text-ellipsis whitespace-nowrap border-divider01 border-l bg-bgPrimaryBase px-4.5 py-2.75 text-M',
        breadcrumbsCount ? 'flex' : 'hidden',
      )}
    >
      {Object.values(breadcrumbs)
        .filter(omitEntityTypes)
        .slice(0, allowedItemsCount || breadcrumbsCount)
        .reverse()
        .map((b, i, arr) => (
          <React.Fragment key={b.id}>
            {arr.length > i + 1 ? (
              <span
                className="group"
                onClick={toggleSettings(b.id)}
                onMouseOver={highlight(b.id)}
                onMouseOut={highlight(undefined)}
              >
                <button
                  type="button"
                  className="relative cursor-pointer px-3.5 text-textTertiary group-hover:text-textSecondary"
                >
                  {t(`entity_settings.${b.type}.header`.toLowerCase())}
                </button>
                <i className="fa-solid fa-chevron-right cursor-pointer text-iconTertiary group-hover:text-iconSecondary" />
              </span>
            ) : (
              <button
                type="button"
                className="relative cursor-pointer pr-3 pl-3.5 text-textLink hover:text-textLinkHover"
                // when you click on a breadcrumb it becomes editable and we need to disable highlight
                onMouseOver={highlight(b.id)}
                onMouseOut={highlight(undefined)}
              >
                {t(`entity_settings.${b.type}.header`.toLowerCase())}
              </button>
            )}
          </React.Fragment>
        ))}
    </div>
  )
}
